import { z } from "zod";
import { BankCashItemType } from "@common/types/bankCashItem";
import {
    monetaryValuesEqual,
    sumMonetaryValues,
} from "@common/helpers/monetary";

export const cashItemSchema = z.object({
    id: z.string().uuid(),
    transactionId: z.number().int(),
    type: z.nativeEnum(BankCashItemType),
    checkNumber: z.string().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export const createCheckLineSchema = z
    .object({
        amount: z.number().multipleOf(0.01).min(0),
        categoryId: z.string().uuid().optional(),
        accountId: z.string().uuid().optional(),
        accrualAccountId: z.string().uuid().optional(),
        description: z.string().trim().max(1000).optional().nullable(),
        classIds: z.array(z.string().uuid()).max(50).optional(),
    })
    .refine(
        ({ categoryId, accountId }) => categoryId != null || accountId != null,
        {
            message: "At least one of categoryId or accountId must be defined.",
            path: ["categoryId"],
        },
    );

const cashItemFieldsSchema = z.object({
    financialAccountId: z.number().int().positive(),
    amount: z.number().positive(),
    checkNumber: z.string().trim().max(64).nullish(),
    counterpartyId: z.string().uuid(),
    memo: z.string().max(1000).optional().nullable(),
    splits: z.array(createCheckLineSchema).min(1).max(100),
});

export type CashItemFields = z.infer<typeof cashItemFieldsSchema>;

interface SplitSumCandidate {
    amount: number;
    splits: Array<{ amount: number }>;
}

function hasBalancedSplits({ amount, splits }: SplitSumCandidate): boolean {
    return monetaryValuesEqual(
        amount,
        sumMonetaryValues(splits.map((split) => split.amount)),
    );
}

const BALANCED_SPLITS_ISSUE = {
    message: "Split amounts must sum to the total amount",
    path: ["splits"],
};

export const createCashItemBodySchema = cashItemFieldsSchema
    .extend({ date: z.coerce.date() })
    .refine(hasBalancedSplits, BALANCED_SPLITS_ISSUE);

export const createCheckResponseSchema = z.object({
    check: cashItemSchema,
});

export const createDepositResponseSchema = z.object({
    deposit: cashItemSchema,
});

export const MAX_CASH_ITEM_BATCH_SIZE = 50;
export const MAX_CASH_ITEM_BATCH_LINES = 500;

export const createCashItemsBatchItemSchema = cashItemFieldsSchema
    .extend({
        type: z.nativeEnum(BankCashItemType),
        dateOnly: z.string().date(),
    })
    .refine(hasBalancedSplits, BALANCED_SPLITS_ISSUE);

export const createCashItemsBatchBodySchema = z.object({
    items: z
        .array(createCashItemsBatchItemSchema)
        .min(1)
        .max(MAX_CASH_ITEM_BATCH_SIZE)
        .refine(
            (items) =>
                items.reduce((total, item) => total + item.splits.length, 0) <=
                MAX_CASH_ITEM_BATCH_LINES,
            {
                message: `A batch can have at most ${MAX_CASH_ITEM_BATCH_LINES} lines in total.`,
            },
        ),
});

export type CashItemBatchItem = z.infer<typeof createCashItemsBatchItemSchema>;

export const CASH_ITEM_ITEM_ERROR_CODES = [
    "not_found",
    "invalid_input",
    "locked_period",
    "reconciled",
    "deletion_not_allowed",
    "internal_error",
] as const;

export type CashItemItemErrorCode = (typeof CASH_ITEM_ITEM_ERROR_CODES)[number];

export const cashItemItemErrorSchema = z.object({
    code: z.enum(CASH_ITEM_ITEM_ERROR_CODES),
    message: z.string(),
});

export const cashItemWriteSchema = z.object({
    id: z.string().uuid(),
    transactionId: z.number().int(),
    type: z.nativeEnum(BankCashItemType),
    checkNumber: z.string().nullable(),
    dateOnly: z.string().date(),
    amount: z.number(),
    financialAccountId: z.number().int(),
    counterpartyId: z.string().uuid(),
    memo: z.string().nullable(),
    clearedDateOnly: z.string().date().nullable(),
    reconciliationId: z.string().uuid().nullable(),
});

export const cashItemListItemSchema = cashItemWriteSchema.extend({
    counterpartyId: z.string().uuid().nullable(),
    financialAccountName: z.string().nullable(),
    counterpartyName: z.string().nullable(),
});

export const cashItemBatchSummarySchema = z.object({
    total: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
});

export const cashItemBatchResultSchema = z.discriminatedUnion("success", [
    z.object({
        itemIndex: z.number().int().nonnegative(),
        success: z.literal(true),
        cashItem: cashItemWriteSchema,
    }),
    z.object({
        itemIndex: z.number().int().nonnegative(),
        success: z.literal(false),
        error: cashItemItemErrorSchema,
    }),
]);

export const createCashItemsBatchResponseSchema = z.object({
    results: z.array(cashItemBatchResultSchema),
    summary: cashItemBatchSummarySchema,
});

export const cashItemListStatusSchema = z.enum(["outstanding", "reconciled"]);

export const listCashItemsQuerySchema = z
    .object({
        status: cashItemListStatusSchema,
        financialAccountId: z.coerce.number().int().positive().optional(),
        type: z.nativeEnum(BankCashItemType).optional(),
        since: z.string().date().optional(),
        until: z.string().date().optional(),
        limit: z.coerce.number().int().min(1).max(100).default(50),
        cursor: z
            .string()
            .regex(/^(0|[1-9]\d*)$/, "Invalid pagination cursor")
            .optional(),
    })
    .refine(
        (query) =>
            query.since === undefined ||
            query.until === undefined ||
            query.since <= query.until,
        {
            message: "until must be on or after since",
            path: ["until"],
        },
    );

export const listCashItemsResponseSchema = z.object({
    items: z.array(cashItemListItemSchema),
    total: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
});

export const MAX_CASH_ITEM_DELETE_SIZE = 50;

export const deleteCashItemsBodySchema = z.object({
    cashItemIds: z
        .array(z.string().uuid())
        .min(1)
        .max(MAX_CASH_ITEM_DELETE_SIZE)
        .refine((ids) => new Set(ids).size === ids.length, {
            message: "Cash item ids must be unique",
        }),
});

export const cashItemDeleteResultSchema = z.discriminatedUnion("success", [
    z.object({
        itemIndex: z.number().int().nonnegative(),
        cashItemId: z.string().uuid(),
        success: z.literal(true),
        transactionId: z.number().int(),
    }),
    z.object({
        itemIndex: z.number().int().nonnegative(),
        cashItemId: z.string().uuid(),
        success: z.literal(false),
        error: cashItemItemErrorSchema,
    }),
]);

export const deleteCashItemsResponseSchema = z.object({
    results: z.array(cashItemDeleteResultSchema),
    summary: cashItemBatchSummarySchema,
});
